import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getSession, getCurrentProfile, signOut, AuthSession, Profile } from '@/services/auth'
import {
  listPptTasks, getPptTask, preflightImageToPpt, createPptTask,
  redeemCouponCode,
  PptTaskRecord, PptTaskDetail, PreflightFile, ImageToPptPreflightResult,
} from '@/services/tasks'
import { uploadBlob } from '@/services/storage'
import { QuotaError } from '@/services/api'
import { createAlipayPagePay, queryAlipayOrder } from '@/services/payments'

export interface StagedFile {
  name: string
  url: string
  storagePath: string
  size: number
  mimeType: string
}

export const useWorkspaceStore = defineStore('workspace', () => {
  const session = ref<AuthSession | null>(null)
  const profile = ref<Profile | null>(null)
  const authModalVisible = ref(false)
  const tasks = ref<PptTaskRecord[]>([])
  const activeTask = ref<PptTaskDetail | null>(null)
  const stagedFiles = ref<StagedFile[]>([])
  const preflightResult = ref<ImageToPptPreflightResult | null>(null)
  const preflightLoading = ref(false)
  const submitting = ref(false)
  const uploading = ref(false)
  const loadingTasks = ref(false)
  const inviteCode = ref('')
  const redeeming = ref(false)
  const redeemMessage = ref('')
  const quotaModalVisible = ref(false)
  const error = ref('')
  const couponModalVisible = ref(false)
  const couponCode = ref<string>('')
  const couponRedeemResult = ref<string>('')
  const couponAutoRedeeming = ref(false)
  const couponRedeemSucceeded = ref(false)
  const alipayPoints = ref(10)
  const alipayPaying = ref(false)
  let redeemCouponAfterAuth = false
  let pollTimer: ReturnType<typeof setInterval> | null = null
  const processingStatuses = ['queued', 'running', 'outline_ready', 'sample_ready']
  const terminalStatuses = ['completed', 'failed', 'cancelled', 'expired']

  async function init() {
    const s = await getSession()
    session.value = s
    if (s) {
      await checkPendingAlipayOrders()
      await loadProfile()
      await loadTasks()
    }
  }

  // 支付结果兜底：异步通知本地不可达时，回来后主动查询未完成订单
  async function checkPendingAlipayOrders() {
    const raw = localStorage.getItem('pendingAlipayOrders')
    if (!raw) return
    let tradeNos: string[]
    try {
      tradeNos = JSON.parse(raw)
    }
    catch {
      localStorage.removeItem('pendingAlipayOrders')
      return
    }
    const remaining: string[] = []
    for (const outTradeNo of tradeNos) {
      try {
        const order = await queryAlipayOrder(outTradeNo)
        if (order.status === 'TRADE_SUCCESS' || order.status === 'TRADE_FINISHED' || order.status === 'CLOSED') continue
        remaining.push(outTradeNo)
      }
      catch {
        remaining.push(outTradeNo)
      }
    }
    if (remaining.length) localStorage.setItem('pendingAlipayOrders', JSON.stringify(remaining))
    else localStorage.removeItem('pendingAlipayOrders')
  }

  async function loadProfile() {
    profile.value = await getCurrentProfile()
  }

  async function loadTasks() {
    if (!session.value) return
    loadingTasks.value = true
    try {
      tasks.value = await listPptTasks()
      // Auto-poll active tasks
      const active = tasks.value.find(t => processingStatuses.includes(t.status))
      if (active && !pollTimer) {
        await selectTask(active.id)
      }
    }
    catch (e) {
      error.value = e instanceof Error ? e.message : '加载任务失败'
    }
    finally {
      loadingTasks.value = false
    }
  }

  async function selectTask(taskId: string) {
    try {
      const detail = await getPptTask(taskId)
      activeTask.value = detail
      tasks.value = tasks.value.map(task => task.id === taskId ? { ...task, ...detail.task } : task)
      if (processingStatuses.includes(detail.task.status)) startPolling()
    }
    catch (e) {
      error.value = e instanceof Error ? e.message : '加载任务详情失败'
    }
  }

  async function addFiles(files: File[]) {
    if (uploading.value) return
    if (!session.value) {
      showAuthModal()
      return
    }
    uploading.value = true
    try {
      for (const file of files) {
        try {
          const uploaded = await uploadBlob(file)
          const { path, url } = uploaded
          const mime = uploaded.mimeType || file.type || 'application/octet-stream'
          stagedFiles.value.push({
            name: file.name, url, storagePath: path,
            size: uploaded.sizeBytes ?? file.size, mimeType: mime,
          })
        }
        catch {
          error.value = `上传 ${file.name} 失败`
        }
      }
      if (stagedFiles.value.length > 0) await doPreflight()
    }
    finally {
      uploading.value = false
    }
  }

  function removeFile(index: number) {
    stagedFiles.value.splice(index, 1)
    if (stagedFiles.value.length === 0) preflightResult.value = null
    else doPreflight()
  }

  function reorderFile(from: number, to: number) {
    const [item] = stagedFiles.value.splice(from, 1)
    stagedFiles.value.splice(to, 0, item)
    doPreflight()
  }

  async function doPreflight(): Promise<boolean> {
    if (stagedFiles.value.length === 0) return false
    preflightLoading.value = true
    error.value = ''
    try {
      const files: PreflightFile[] = stagedFiles.value.map(f => ({
        url: f.url, storagePath: f.storagePath, name: f.name, mimeType: f.mimeType, size: f.size,
      }))
      preflightResult.value = await preflightImageToPpt(files)
      return true
    }
    catch (e) {
      error.value = e instanceof Error ? e.message : '预检失败'
      preflightResult.value = null
      return false
    }
    finally {
      preflightLoading.value = false
    }
  }

  async function submitTask() {
    if (!session.value || !preflightResult.value || stagedFiles.value.length === 0) return
    submitting.value = true
    error.value = ''
    try {
      const preflightOk = await doPreflight()
      if (!preflightOk || !preflightResult.value) return
      const task = await createPptTask({
        prompt: '图转可编辑 PPT',
        mode: 'image_to_editable_ppt',
        preflightTicket: preflightResult.value.preflightTicket,
      })
      stagedFiles.value = []
      preflightResult.value = null
      await loadTasks()
      await selectTask(task.id)
      await loadProfile()
    }
    catch (e) {
      if (e instanceof QuotaError) {
        quotaModalVisible.value = true
        error.value = e.message
      }
      else {
        error.value = e instanceof Error ? e.message : '提交任务失败'
      }
    }
    finally {
      submitting.value = false
    }
  }

  function startPolling() {
    if (pollTimer) return
    pollTimer = setInterval(async () => {
      if (!activeTask.value) {
        stopPolling(); return
      }
      const status = activeTask.value.task.status
      if (terminalStatuses.includes(status)) {
        stopPolling()
        await loadTasks()
        await loadProfile()
        return
      }
      await selectTask(activeTask.value.task.id)
    }, 3000)
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer); pollTimer = null
    }
  }

  async function doRedeem() {
    const code = inviteCode.value.trim()
    if (!code) return
    redeeming.value = true
    redeemMessage.value = ''
    try {
      const result = await redeemCouponCode(code)
      redeemMessage.value = `兑换成功，获得 ${result.grantedPoints} 额度`
      inviteCode.value = ''
      await loadProfile()
    }
    catch (e) {
      redeemMessage.value = e instanceof Error ? e.message : '兑换失败'
    }
    finally {
      redeeming.value = false
    }
  }

  async function logout() {
    stopPolling()
    await signOut()
    session.value = null
    profile.value = null
    tasks.value = []
    activeTask.value = null
    stagedFiles.value = []
    preflightResult.value = null
  }

  function showAuthModal() {
    authModalVisible.value = true
  }
  function hideAuthModal() {
    authModalVisible.value = false
    if (redeemCouponAfterAuth) {
      redeemCouponAfterAuth = false
      couponModalVisible.value = true
    }
  }
  function showQuotaModal() {
    quotaModalVisible.value = true
  }
  function hideQuotaModal() {
    quotaModalVisible.value = false
  }

  async function buyPointsWithAlipay() {
    const points = Number(alipayPoints.value)
    if (!Number.isInteger(points) || points < 1 || points > 10000) {
      redeemMessage.value = '请输入 1 至 10000 的整数额度'
      return
    }
    alipayPaying.value = true
    redeemMessage.value = ''
    try {
      const order = await createAlipayPagePay(points)
      const pending = JSON.parse(localStorage.getItem('pendingAlipayOrders') || '[]') as string[]
      pending.push(order.outTradeNo)
      localStorage.setItem('pendingAlipayOrders', JSON.stringify(pending.slice(-20)))
      const wrapper = document.createElement('div')
      wrapper.innerHTML = order.paymentHtml
      const form = wrapper.querySelector('form')
      if (!form) throw new Error('支付宝支付表单生成失败')
      form.style.display = 'none'
      document.body.appendChild(form)
      form.submit()
    }
    catch (e) {
      redeemMessage.value = e instanceof Error ? e.message : '创建支付宝订单失败'
    }
    finally {
      alipayPaying.value = false
    }
  }

  function showCouponModal() {
    couponCode.value = ''
    couponRedeemResult.value = ''
    couponAutoRedeeming.value = false
    couponRedeemSucceeded.value = false
    redeemCouponAfterAuth = false
    couponModalVisible.value = true
  }

  function hideCouponModal() {
    couponModalVisible.value = false
    couponCode.value = ''
    couponRedeemResult.value = ''
    couponAutoRedeeming.value = false
    couponRedeemSucceeded.value = false
    redeemCouponAfterAuth = false
  }

  async function onAuthSuccess() {
    const shouldRedeemCoupon = redeemCouponAfterAuth
    redeemCouponAfterAuth = false
    await init()
    if (shouldRedeemCoupon && couponCode.value.trim()) {
      couponModalVisible.value = true
      couponAutoRedeeming.value = true
      await doCouponRedeem(couponCode.value)
      couponAutoRedeeming.value = false
    }
  }

  async function requestCouponRedeem() {
    const code = couponCode.value.trim()
    if (!code) {
      couponRedeemResult.value = '请输入兑换码'
      return
    }
    couponCode.value = code
    couponRedeemResult.value = ''
    couponRedeemSucceeded.value = false
    if (!session.value) {
      redeemCouponAfterAuth = true
      couponModalVisible.value = false
      showAuthModal()
      return
    }
    await doCouponRedeem(code)
  }

  async function doCouponRedeem(code: string) {
    redeeming.value = true
    couponRedeemResult.value = ''
    couponRedeemSucceeded.value = false
    try {
      const result = await redeemCouponCode(code)
      couponRedeemResult.value = `已兑换成 ${result.grantedPoints} 额度，当前余额 ${result.balance}`
      couponRedeemSucceeded.value = true
      await loadProfile()
      return true
    }
    catch (e) {
      couponRedeemResult.value = e instanceof Error ? e.message : '兑换失败'
      couponRedeemSucceeded.value = false
      return false
    }
    finally {
      redeeming.value = false
    }
  }

  function checkCouponCode() {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (code) {
      couponCode.value = code
      couponRedeemResult.value = ''
      couponAutoRedeeming.value = false
      couponRedeemSucceeded.value = false
      couponModalVisible.value = true
      window.history.replaceState({}, '', window.location.pathname)
    }
  }

  return {
    session, profile, authModalVisible, tasks, activeTask, stagedFiles,
    preflightResult, preflightLoading, submitting, uploading, loadingTasks,
    inviteCode, redeeming, redeemMessage, quotaModalVisible, error,
    couponModalVisible, couponCode, couponRedeemResult,
    couponAutoRedeeming, couponRedeemSucceeded,
    alipayPoints, alipayPaying,
    init, loadProfile, loadTasks, selectTask, addFiles, removeFile, reorderFile,
    doPreflight, submitTask, startPolling, stopPolling,
    doRedeem, logout, showAuthModal, hideAuthModal, showQuotaModal, hideQuotaModal,
    showCouponModal, hideCouponModal, onAuthSuccess, requestCouponRedeem,
    doCouponRedeem, checkCouponCode, buyPointsWithAlipay,
  }
})
